/**
 * RG-06 — organization resolution integrity contract.
 *
 * Engineering invariant (mandatory):
 *
 * Every organization-scoped operation must resolve tenant ownership through the
 * canonical organization resolution hierarchy.
 *
 * If organization ownership cannot be positively established, the operation
 * must fail.
 *
 * Silent fallbacks are prohibited except where an explicitly documented
 * architectural exception exists (e.g. invitations / membership tie-break via
 * `allowDefaultOrganization` or direct `getDefaultOrganizationId`).
 *
 * Canonical hierarchy (operational queries):
 *   1. explicit organizationId
 *   2. actor.organization_id (membership enrichment)
 *   3. FAIL — never guess, never substitute another organization
 *
 * See docs/engineering/rg06_fail_closed_organization_resolution.md,
 * docs/engineering/system_overview.md, and docs/architecture_backlog.md.
 */

export const RG06_ORG_REQUIRED_ERROR_CODE = "RG06_ORG_REQUIRED";

export const RG06_ORGANIZATION_RESOLUTION_INVARIANT = Object.freeze({
  useCanonicalHierarchy: true,
  failClosedWithoutOwnership: true,
  silentFallbacksProhibited: true,
  documentedExceptionsOnly: true,
  hierarchy: Object.freeze([
    "explicit organizationId",
    "actor.organization_id",
    "FAIL",
  ]),
});

/**
 * Expose org-resolution helpers for Playwright RG-06 regressions.
 * Enabled only when REACT_APP_E2E_ORG_RESOLUTION_HOOKS=true (playwright webServer).
 */
export function exposeOrgResolutionE2eHooks(hooks) {
  if (
    typeof window === "undefined" ||
    process.env.REACT_APP_E2E_ORG_RESOLUTION_HOOKS !== "true"
  ) {
    return;
  }

  window.__CP_ORG_RESOLUTION__ = {
    ...hooks,
    RG06_ORG_REQUIRED_ERROR_CODE,
    RG06_ORGANIZATION_RESOLUTION_INVARIANT,
  };
}
